import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Property } from '../types';
import { formatPrice } from '../lib/format';

interface PropertyCardProps {
  property: Property;
}

export const propertyPagePath = (property: Property) => {
  const code = property.property_code || String(property.id);
  if (property.listing_type === 'management') return `/management/${code}`;
  if (property.listing_type === 'sale') return `/sales/${code}`;
  return `/leasing/${code}`;
};

export default function PropertyCard({ property }: PropertyCardProps) {
  const [imageIndex, setImageIndex] = useState(0);

  const images = (property.images?.length ? property.images : [property.main_image]).filter((x): x is string => !!x);
  const currentImage = images[Math.min(imageIndex, images.length - 1)];
  const location = property.area;

  return (
    <div className="block group">
      <div className="relative rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
        <Link to={propertyPagePath(property)} className="relative block h-56 sm:h-64 md:h-72 bg-gray-100">
          {currentImage && (
            <img
              src={currentImage}
              alt={property.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageIndex((imageIndex - 1 + images.length) % images.length); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageIndex((imageIndex + 1) % images.length); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70"
                aria-label="Next image"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {property.is_sold && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
              <span className="bg-red-600 text-white px-6 py-2 rounded-lg text-lg font-bold tracking-wider">SOLD</span>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 z-10 px-4 pt-12 pb-3 bg-gradient-to-t from-black/85 via-black/45 to-transparent pointer-events-none">
            <p className="text-white text-[15px] font-bold leading-tight">
              {formatPrice(property.price, property.currency)}
            </p>
            <p className="mt-2 text-right text-[11px] font-medium uppercase tracking-[0.2em] text-white leading-relaxed whitespace-nowrap overflow-hidden text-ellipsis">
              {property.bedrooms} {property.bedrooms === 1 ? 'Bed' : 'Beds'} | {property.bathrooms} {property.bathrooms === 1 ? 'Bath' : 'Baths'} | {location}
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
}
